import { ModbusRTU } from './ModbusRTU';

export interface FrameCriteria {
  slaveId?: number;
  functionCode?: number;
}

const MAX_BUFFER = 256;

/**
 * Remonta frames Modbus RTU a partir de chunks arbitrários recebidos do
 * transporte (Web Serial, WebSocket, etc.). Cada frame é validado por CRC16.
 *
 * Uso: alimente o assembler com push(bytes) e consulte next(criteria) para
 * obter frames completos e válidos. Frames sem CRC válido ou que não batem
 * com os critérios de correlação são descartados automaticamente (rescan).
 */
export class FrameAssembler {
  private buffer: number[] = [];

  push(bytes: Uint8Array): void {
    for (let i = 0; i < bytes.length; i++) {
      this.buffer.push(bytes[i]);
    }
  }

  reset(): void {
    this.buffer = [];
  }

  get bufferedBytes(): number {
    return this.buffer.length;
  }

  next(criteria?: FrameCriteria): Uint8Array | null {
    while (true) {
      const frame = this.extract();
      if (!frame) return null;
      if (this.matches(frame, criteria)) return frame;
    }
  }

  private extract(): Uint8Array | null {
    while (this.buffer.length > 0) {
      if (this.buffer.length < 3) return null;

      const len = this.expectedLength(this.buffer);
      if (len === null) {
        if (this.buffer.length >= MAX_BUFFER) {
          this.buffer.shift();
          continue;
        }
        this.buffer.shift();
        continue;
      }
      if (this.buffer.length < len) return null;

      const bytes = this.buffer.slice(0, len);
      this.buffer = this.buffer.slice(len);

      const body = new Uint8Array(bytes.slice(0, len - 2));
      const crcFrame = (bytes[len - 1] << 8) | bytes[len - 2];
      if (ModbusRTU.calculateCRC(body) !== crcFrame) continue;

      return new Uint8Array(bytes);
    }
    return null;
  }

  private expectedLength(buffer: number[]): number | null {
    const functionCode = buffer[1];

    if (functionCode & 0x80) return 5;

    switch (functionCode) {
      case 0x03:
      case 0x04:
        return 3 + buffer[2] + 2;
      case 0x06:
      case 0x10:
        return 8;
      default:
        return null;
    }
  }

  private matches(frame: Uint8Array, criteria?: FrameCriteria): boolean {
    if (!criteria) return true;
    if (criteria.slaveId !== undefined && frame[0] !== criteria.slaveId) {
      return false;
    }
    if (criteria.functionCode !== undefined) {
      if ((frame[1] & 0x7f) !== criteria.functionCode) return false;
    }
    return true;
  }
}
